/**
 * @prettier
 */

import ApiService from "../../services/api/api";
import logService from "../../services/shared/logService";
import DataDogService from "../../services/shared/datadogService";

// Action Types
export const POA_DOCUMENTS_LOADING = "POA_DOCUMENTS_LOADING";
export const POA_DOCUMENTS_LOADED = "POA_DOCUMENTS_LOADED";
export const POA_DOCUMENTS_ERROR = "POA_DOCUMENTS_ERROR";
export const POA_DOCUMENT_UPLOADED = "POA_DOCUMENT_UPLOADED";
export const POA_SET_COMPLETED = "POA_SET_COMPLETED";
export const POA_RESET_STATE = "POA_RESET_STATE";
export const POA_SET_LOADING = "POA_SET_LOADING";
export const POA_LOAD_DOCUMENTS = "POA_LOAD_DOCUMENTS";

/**
 * Set loading state for POA operations
 * @param {boolean} isLoading - Whether POA operations are loading
 * @returns {Object} - Action object
 */
export function setPoaLoading(isLoading = true) {
    return {
        type: POA_SET_LOADING,
        payload: { isLoading },
    };
}

/**
 * Load documents into state without fetching from API
 * @param {Array} poaDocuments - Proof of address documents to load
 * @param {Array} additionalDocuments - Additional documents to load
 * @returns {Object} - Action object
 */
export function loadDocuments(poaDocuments = [], additionalDocuments = []) {
    // Process documents to create status maps for both document types
    const poaStatusMap = processDocumentsToMap(poaDocuments);
    const additionalStatusMap = processDocumentsToMap(additionalDocuments);

    // Check document requirements separately for each type
    const poaRequiredUploaded = checkAllRequiredDocumentsUploaded(poaDocuments);
    const additionalRequiredUploaded =
        checkAllRequiredDocumentsUploaded(additionalDocuments);

    // Combined flag for backward compatibility
    const allRequiredUploaded =
        poaRequiredUploaded &&
        (additionalDocuments.length === 0 || additionalRequiredUploaded);

    // Return the documents loaded action
    return {
        type: POA_DOCUMENTS_LOADED,
        payload: {
            documents: poaDocuments,
            additionalDocuments: additionalDocuments,
            poaStatusMap: poaStatusMap,
            additionalStatusMap: additionalStatusMap,
            allRequiredUploaded,
            poaRequiredUploaded,
            additionalRequiredUploaded,
        },
    };
}

/**
 * Fetch proof of address documents status
 * @returns {Function} - Thunk action
 */
export function fetchPoaDocumentStatus() {
    return dispatch => {
        // Set loading state to true
        dispatch({ type: POA_DOCUMENTS_LOADING });

        return ApiService.getProofOfAddressStatus()
            .then(response => {
                if (response && response.data) {
                    const poaDocuments = response.data.poa_documents || [];
                    const additionalDocuments =
                        response.data.additional_documents || [];

                    // Use the loadDocuments action to process and load the documents
                    const action = loadDocuments(
                        poaDocuments,
                        additionalDocuments
                    );
                    dispatch(action);

                    return {
                        poaStatusMap: action.payload.poaStatusMap,
                        additionalStatusMap: action.payload.additionalStatusMap,
                        allRequiredUploaded: action.payload.allRequiredUploaded,
                    };
                }
                return null;
            })
            .catch(error => {
                logService.error("Error fetching POA document status:", error);
                DataDogService.createError(
                    "Failed to fetch proof of address document status"
                );

                dispatch({
                    type: POA_DOCUMENTS_ERROR,
                    payload: error,
                });

                return Promise.reject(error);
            });
    };
}

/**
 * Set a document as uploaded by ID
 * @param {string} documentId - The ID of the document that was uploaded
 * @param {boolean} isPoaDocument - Whether this is a POA document (true) or an additional document (false)
 * @returns {Object} - Action object
 */
export function setDocumentUploaded(documentId, isPoaDocument = true) {
    return {
        type: POA_DOCUMENT_UPLOADED,
        payload: {
            documentId,
            isPoaDocument,
        },
    };
}

/**
 * Set all POA requirements as completed
 * @param {boolean} isCompleted - Explicitly set the completion status (optional)
 * @returns {Object} - Action object
 */
export function setPoaCompleted(isCompleted) {
    return {
        type: POA_SET_COMPLETED,
        payload: { isCompleted },
    };
}

/**
 * Reset the POA documents state
 * @returns {Object} - Action object
 */
export function resetPoaState() {
    return {
        type: POA_RESET_STATE,
    };
}

// Helper function to process documents and create a status map
function processDocumentsToMap(documents) {
    const statusMap = {};

    function processDocuments(docs) {
        if (!docs) return;

        docs.forEach(doc => {
            statusMap[doc.poa_upload_key] = doc.isUploaded;

            if (doc.child && doc.child.length > 0) {
                processDocuments(doc.child);
            }
        });
    }

    processDocuments(documents);
    return statusMap;
}

// Helper function to check if all required documents are uploaded
function checkAllRequiredDocumentsUploaded(docs) {
    if (!docs) return true;

    // First, check all top-level documents to ensure each meets its requirements
    for (const doc of docs) {
        // Check if this document itself is required but not uploaded
        if (doc.is_required && !doc.isUploaded) {
            return false;
        }

        // Check if this document has a min_required_child constraint
        if (
            doc.child &&
            doc.child.length > 0 &&
            doc.min_required_child &&
            doc.min_required_child > 0
        ) {
            // Count how many child documents have been uploaded
            const uploadedChildCount = doc.child.filter(
                childDoc => childDoc.isUploaded
            ).length;

            // If fewer than min_required_child documents are uploaded, return false
            if (uploadedChildCount < doc.min_required_child) {
                return false;
            }
        }
    }

    // Now check children recursively
    for (const doc of docs) {
        if (doc.child && doc.child.length > 0) {
            // Check if any required children are not uploaded
            const requiredChildrenUploaded = doc.child.every(
                childDoc => !childDoc.is_required || childDoc.isUploaded
            );

            if (!requiredChildrenUploaded) {
                return false;
            }

            // Check children recursively for their own requirements
            const childrenComplete = checkAllRequiredDocumentsUploaded(
                doc.child
            );
            if (!childrenComplete) {
                return false;
            }
        }
    }

    // If we get here, all requirements are satisfied across the entire document tree
    return true;
}
